import React, { useMemo } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { Trans } from "@coworker/locales";
import {
  getActiveTask,
  calculateNumberOfVotes,
} from "./QuestionsDetailPage.helper";
import { Question } from "../BackendApiRepository/QuestionResponse";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { usePendingMfaqTasks, mfaqTaskKey } from "../hooks/usePendingMfaqTasks";
import { formatDateByLocale } from "@coworker/reusable/formatters/date.formatter";

interface MfaqTaskButtonProps {
  question: Question;
  filters: any; //TODO: Find out if we have type for this
  periodStartDate?: string;
  periodEndDate?: string;
}

const MfaqTaskButtonComponent: React.FC<MfaqTaskButtonProps> = ({
  question,
  filters,
  periodStartDate,
  periodEndDate,
}) => {
  const { toggleModal } = React.useContext(ModalContext);

  const [, setSearchParams] = useSearchParamState();
  const { data: taskTypes } = useAllTaskTypes();
  const mfaqFollowUpId = useMemo(
    () =>
      taskTypes?.find(({ name }) => name === TaskTypeNames.MFAQ_FOLLOW_UP)?.id,
    [taskTypes]
  );

  // v2: look up the pending task from tasks-v2 by (question_id, area).
  const { pendingTaskByKey } = usePendingMfaqTasks({ enabled: true });
  const v2PendingTask =
    pendingTaskByKey.get(mfaqTaskKey(question.question_id, filters[1] ?? "")) ??
    null;

  // v1: read from the task list embedded in the question object by the MFAQ service.
  const { hasOngoingTask: v1HasOngoingTask, taskId: v1TaskId } = getActiveTask(
    question,
    filters[1]
  );

  // Either source constitutes an ongoing task. v2 takes priority if both coexist.
  const isV2Task = !!v2PendingTask;
  const hasOngoingTask = isV2Task || v1HasOngoingTask;
  const taskId = v2PendingTask?.id ?? v1TaskId ?? null;
  const taskString = hasOngoingTask ? "showTaskString" : "createTaskString";

  const createMFAQTask = () => {
    if (mfaqFollowUpId) {
      setSearchParams((prev) => {
        prev.set("taskCreator", String(mfaqFollowUpId));
        prev.set(
          "initialValues",
          JSON.stringify({
            details: {
              question_text: question.question_text,
              question_id: question.question_id,
              question_area: filters[1] ?? "",
              upvotes: calculateNumberOfVotes(question, filters[1] ?? "") ?? 0,
              upvotes_period:
                filters[0] === "period=custom" &&
                periodStartDate &&
                periodEndDate
                  ? `${formatDateByLocale(periodStartDate)} - ${formatDateByLocale(periodEndDate)}`
                  : (filters[0] ?? ""),
            },
          })
        );
      });
    }
  };

  const showMFAQTask = () => {
    if (isV2Task) {
      setSearchParams((prev) => {
        prev.set("taskViewer", taskId!);
      });
    } else {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId: taskId, taskType: TaskType.MFAQ_FOLLOW_UP },
      });
    }
  };

  return (
    <FixaButton
      data-testid="createMFAQTask"
      type="primary"
      text={<Trans>{taskString}</Trans>}
      onClick={taskId ? showMFAQTask : createMFAQTask}
      disabled={!hasOngoingTask && mfaqFollowUpId === undefined}
    />
  );
};

export default MfaqTaskButtonComponent;
